import {
  FunctionComponent,
  useRef,
  useState,
  ReactNode,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';

import { getOS } from '../../../utilities/helpers/common/system';
import DownloadPdfForm from './DownloadPdfForm';
import ErrorBoundaryMessage from '../../ui/ErrorBoundaryMessage';
import { Document, Page, pdfjs } from 'react-pdf';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import Raptor2Loading from '../../feedback/Raptor2Loading';
import { IframeFallback } from './IframeFallback';
import { Tooltip } from '@mui/material';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const MIN_ZOOM = 25;
const MAX_ZOOM = 400;
const ZOOM_STEP = 10;
const A4_ASPECT_RATIO = 297 / 210; // height / width

export interface RaptorPDFViewerHandle {
  scrollTo: (scrollTop: number) => void;
  getScrollTop: () => number;
}

interface RaptorPDFViewerProps {
  pdfUrl: string | null;
  fileName: string;
  hasError?: boolean;
  errorMessage: string;
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
  isPending?: boolean;
  customToolbarComponents?: ReactNode;
  afterDownloadComponents?: ReactNode;
  onScroll?: (scrollTop: number) => void;
  className?: string;
  fitOnLoad?: boolean;
}

const RaptorPDFViewer = forwardRef<
  RaptorPDFViewerHandle,
  RaptorPDFViewerProps
>(
  (
    {
      pdfUrl,
      fileName,
      hasError,
      errorMessage,
      setHasError,
      isPending,
      customToolbarComponents,
      afterDownloadComponents,
      onScroll,
      className,
      fitOnLoad,
    },
    ref,
  ) => {
    const [numPages, setNumPages] = useState<number>(0);
    const [zoom, setZoom] = useState('100');
    const [containerWidth, setContainerWidth] = useState(800);
    // When fitOnLoad is used, this stores the one-time computed fit-to-height pixel
    // width. pageWidth is then based on this fixed value rather than containerWidth,
    // so resizing the window doesn't alter the PDF size.
    const [fitBaseWidth, setFitBaseWidth] = useState<number | null>(null);
    const pdfContainerRef = useRef<HTMLDivElement | null>(null);
    const scrollPosRef = useRef<number>(0);
    const isInternalScrollRef = useRef(false);
    const fitOnLoadApplied = useRef(false);
    const prefersIframe = getOS().toLowerCase() === 'linux';
    const [useIframeFallback, setUseIframeFallback] = useState(prefersIframe);

    useEffect(() => {
      setUseIframeFallback(prefersIframe);
    }, [pdfUrl, prefersIframe]);

    const zoomValue = parseInt(zoom, 10) || 100;
    const pageWidth = ((fitBaseWidth ?? containerWidth) * zoomValue) / 100;

    useImperativeHandle(ref, () => ({
      scrollTo: (scrollTop: number) => {
        if (pdfContainerRef.current) {
          isInternalScrollRef.current = true;
          pdfContainerRef.current.scrollTop = scrollTop;
          scrollPosRef.current = scrollTop;
          // Reset flag after scroll event completes
          setTimeout(() => {
            isInternalScrollRef.current = false;
          }, 0);
        }
      },
      getScrollTop: () => pdfContainerRef.current?.scrollTop || 0,
    }));

    const handleScroll = () => {
      if (pdfContainerRef.current) {
        scrollPosRef.current = pdfContainerRef.current.scrollTop;
        // Only call onScroll if this is not an internal programmatic scroll
        if (!isInternalScrollRef.current && onScroll) {
          onScroll(pdfContainerRef.current.scrollTop);
        }
      }
    };

    useEffect(() => {
      const observer = new ResizeObserver(() => {
        if (pdfContainerRef.current) {
          // Account for padding (p-5 = 40px total)
          const width = pdfContainerRef.current.clientWidth - 40;
          setContainerWidth(width || 800);

          if (fitOnLoad && !fitOnLoadApplied.current && width > 0) {
            fitOnLoadApplied.current = true;
            const height = pdfContainerRef.current.clientHeight - 40;
            // Compute the page pixel width that makes the A4 page fit the container
            // height exactly at 100% zoom. From this point the zoom control scales
            // relative to this fixed base, not the container width, so resizing the
            // window has no effect on rendered PDF size.
            const fitWidth = Math.floor(height / A4_ASPECT_RATIO);
            setFitBaseWidth(fitWidth);
          }
        }
      });

      if (pdfContainerRef.current) {
        observer.observe(pdfContainerRef.current);
      }

      return () => {
        observer.disconnect();
      };
    }, []);

    const handleZoomIn = () => {
      const newZoom = Math.min(zoomValue + ZOOM_STEP, MAX_ZOOM);
      setZoom(newZoom.toString());
    };

    const handleZoomOut = () => {
      const newZoom = Math.max(zoomValue - ZOOM_STEP, MIN_ZOOM);
      setZoom(newZoom.toString());
    };

    const handleResetZoom = () => {
      setZoom('100');
    };

    const handleZoomChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.replace(/[^\d]/g, '');
      setZoom(value);
    };

    const handleZoomBlur = () => {
      let numValue = parseInt(zoom, 10);
      if (isNaN(numValue)) numValue = 100;

      numValue = Math.max(Math.min(numValue, MAX_ZOOM), MIN_ZOOM);
      setZoom(numValue.toString());
    };

    const handleZoomKeyPress = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleZoomBlur();
      }
    };

    const renderLoadingUI = (
      <div className="mx-auto my-[0.62rem] flex min-h-[1123px] min-w-[794px] items-center justify-center bg-[#dddddd] shadow-[0_1px_3px_rgba(0,0,0,0.1)]">
        <Raptor2Loading centerWrap messages={['Generating Document...']} />
      </div>
    );

    const onDocumentLoadSuccess = (pdf: any) => {
      setNumPages(pdf.numPages);
      if (pdfContainerRef.current) {
        pdfContainerRef.current.scrollTop = scrollPosRef.current;
      }
    };

    return (
      <div className={`flex w-full flex-col overflow-hidden rounded-lg border border-gray-300 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.1)] ${className ?? 'h-[calc(100vh-8.75rem)]'}`}>
        <div className="flex min-h-12 flex-shrink-0 flex-wrap items-center justify-between gap-y-2 border-b border-gray-200 px-[0.62rem] py-[0.47rem]">
          {/* Left group - Zoom controls */}
          {useIframeFallback ? null : (
            <div className="flex shrink-0 items-center gap-2">
              <div className="flex items-center gap-0.5">
                <Tooltip title="Zoom Out" placement="top" arrow>
                  <span className="inline-flex">
                    <button
                      className="flex h-7 w-7 items-center justify-center rounded border border-gray-200 bg-white text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500/30 disabled:cursor-not-allowed disabled:opacity-30"
                      onClick={handleZoomOut}
                      disabled={zoomValue <= MIN_ZOOM}
                      aria-label="Zoom Out"
                      type="button"
                    >
                      <ZoomOutIcon sx={{ fontSize: 16 }} />
                    </button>
                  </span>
                </Tooltip>

                <div className="relative mx-1">
                  <input
                    className="h-7 w-14 rounded border border-gray-200 bg-white px-1.5 text-center text-xs focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400/30"
                    value={zoom}
                    onChange={handleZoomChange}
                    onBlur={handleZoomBlur}
                    onKeyDown={handleZoomKeyPress}
                    aria-label="Zoom Percentage"
                  />
                  <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                    %
                  </span>
                </div>

                <Tooltip title="Zoom In" placement="top" arrow>
                  <span className="inline-flex">
                    <button
                      className="flex h-7 w-7 items-center justify-center rounded border border-gray-200 bg-white text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500/30 disabled:cursor-not-allowed disabled:opacity-30"
                      onClick={handleZoomIn}
                      disabled={zoomValue >= MAX_ZOOM}
                      aria-label="Zoom In"
                      type="button"
                    >
                      <ZoomInIcon sx={{ fontSize: 16 }} />
                    </button>
                  </span>
                </Tooltip>

                <Tooltip title="Reset Zoom" placement="top" arrow>
                  <button
                    className="ml-2 flex h-7 w-7 items-center justify-center rounded border border-gray-200 bg-white text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500/30 disabled:cursor-not-allowed disabled:opacity-30"
                    onClick={handleResetZoom}
                    // disabled={1}
                    aria-label="Reset Zoom"
                    type="button"
                  >
                    <RestartAltIcon sx={{ fontSize: 16 }} />
                  </button>
                </Tooltip>
              </div>
            </div>
          )}
          {/* Right group - Action buttons; min-w-0 allows shrink so content can wrap */}
          <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-[0.47rem]">
            {customToolbarComponents}
            <DownloadPdfForm pdfUrl={pdfUrl} fileName={fileName} />
            {afterDownloadComponents}
          </div>
        </div>

        <div
          className="scrollbar-custom min-h-0 w-full flex-1 overflow-auto bg-[#dddddd] p-5"
          ref={pdfContainerRef}
          onScroll={handleScroll}
          style={{ scrollbarGutter: 'stable both-edges' }}
        >
          {hasError ? (
            <div className="mx-auto my-[0.62rem] flex min-h-[1123px] min-w-[794px] items-center justify-center bg-white shadow-[0_1px_3px_rgba(0,0,0,0.1)]">
              <ErrorBoundaryMessage message={errorMessage} />
            </div>
          ) : useIframeFallback ? (
            <IframeFallback
              filename={fileName}
              isPending={isPending || false}
              pdfUrl={pdfUrl}
            />
          ) : (
            <>
              {(isPending || !pdfUrl) && renderLoadingUI}
              {pdfUrl && !hasError ? (
                <Document
                  key={pdfUrl}
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={() => setUseIframeFallback(true)}
                  loading={renderLoadingUI}
                  className="flex flex-col items-center"
                >
                  {Array.from(new Array(numPages), (el, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      width={pageWidth}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      renderMode="canvas"
                      className="mx-auto my-[0.62rem] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.1)]"
                    />
                  ))}
                </Document>
              ) : null}
            </>
          )}
        </div>
      </div>
    );
  },
);

RaptorPDFViewer.displayName = 'RaptorPDFViewer';

export default RaptorPDFViewer;
